/**
 * Typed HTTP client for the NestJS API.
 *
 * Default base is the RELATIVE `/api` prefix: in every deployment the ingress
 * routes `/api/*` to the api service and strips the prefix (charts/subs-mgmt),
 * so the browser talks to the same origin and no API host is baked into the
 * bundle. `VITE_API_URL` is only set for local contours — Vite's dev server
 * proxies `/api` to :3001 (see vite.config.ts).
 */

import type {
  AccountKind,
  BalanceEntryKind,
  NotificationKind,
  PaymentFlow,
  RateSource,
  RenewalPeriod,
  SubscriptionStatus,
  TransactionType,
} from '@subs/shared';

import type {
  AppRole,
  AppUser,
  AuditEntry,
  AuthMe,
  BackupSnapshot,
  BalanceEntry,
  BalanceReport,
  BillFile,
  BillUploadResult,
  ForecastItem,
  ForecastResponse,
  HealthResponse,
  MoneyAccount,
  Notification,
  Paginated,
  PaymentEvent,
  PlaintextCredentials,
  PublicConfig,
  ReimbursementReport,
  ReimbursementRow,
  Subscription,
  Transaction,
  VendorGroup,
} from './types';

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

export type {
  AppRole,
  AppUser,
  AuditEntry,
  AuthMe,
  BackupSnapshot,
  BalanceEntry,
  BalanceReport,
  BillFile,
  BillUploadResult,
  ForecastItem,
  ForecastResponse,
  HealthResponse,
  MoneyAccount,
  Notification,
  Paginated,
  PaymentEvent,
  PlaintextCredentials,
  PublicConfig,
  ReimbursementReport,
  ReimbursementRow,
  Subscription,
  VendorGroup,
};

/** An API error with its HTTP status and the NestJS message/body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly method: string,
    readonly path: string,
    message: string,
    /** The parsed response body — `message` alone throws away the details. */
    readonly body?: unknown,
  ) {
    super(`${method} ${path} failed with HTTP ${status}: ${message}`);
    this.name = 'ApiError';
  }
}

/**
 * What the receipt-dedup 409 carries: the leg already holding that receipt №,
 * plus the payment + subscription it sits under (joined server-side through
 * `paymentEventId`), so the UI can say WHAT it would duplicate.
 */
export interface DuplicateSourceHashConflict {
  code: 'DUPLICATE_SOURCE_HASH';
  conflicting: Transaction & {
    payment: {
      id: string;
      occurredAt: string;
      paidAmount: number;
      paidCurrency: string;
      subscriptionId: string | null;
      subscriptionName: string | null;
    } | null;
  };
}

/**
 * Reads the dedup 409 out of a thrown error, or `null` for anything else.
 * Every other failure keeps its plain-message handling.
 */
export function duplicateSourceHashConflict(error: unknown): DuplicateSourceHashConflict | null {
  const body = (error as { body?: unknown } | null)?.body as
    | { code?: unknown; conflicting?: unknown }
    | undefined;
  if (!body || body.code !== 'DUPLICATE_SOURCE_HASH') return null;
  if (typeof body.conflicting !== 'object' || body.conflicting === null) return null;
  return { code: 'DUPLICATE_SOURCE_HASH', conflicting: body.conflicting as DuplicateSourceHashConflict['conflicting'] };
}

/** Anything serialisable into a query string; `undefined`/`null`/'' drop the key. */
export type QueryValue = string | number | boolean | null | undefined;

function buildQuery(params: object = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, QueryValue>)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as unknown;
    return readErrorMessage(body) ?? JSON.stringify(body);
  } catch {
    return response.statusText || 'unknown error';
  }
}

/**
 * Fired by {@link handleUnauthorized} when a data endpoint answers 401. The
 * shell listens for it and swaps the page for the signed-out card with an
 * explicit "Sign in" button. A window event because `client.ts` is
 * framework-agnostic: it must not reach into React Query or the router.
 */
export const SESSION_EXPIRED_EVENT = 'subs:session-expired';

/**
 * `AUTH_DISABLED=false`: a 401 means the session is gone. The browser is NOT
 * bounced into the OAuth flow: a full-page redirect to `/auth/login` was the
 * old behaviour, and whenever the callback hop fails (Yandex error, state
 * mismatch, api hiccup) it 302s silently back to `/`, the app 401s again and
 * the user is stuck in an invisible loop that ends on a raw error with no way
 * to log in. Instead the shell is told: it renders the signed-out card and the
 * login starts from an explicit click. `/auth/me` is excluded: it reports
 * `authenticated:false` as plain data (HTTP 200) instead of erroring, and it
 * must never be the thing that starts a login — that is the silent re-login
 * the `/logged-out` interstitial exists to break.
 */
function handleUnauthorized(path: string): void {
  if (path.startsWith('/auth')) return;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

async function request<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(path);
    const body = (await response.json().catch(() => undefined)) as unknown;
    const message = readErrorMessage(body) ?? response.statusText;
    throw new ApiError(response.status, method, path, message, body);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** NestJS puts a string in `message`, an array on validation failures. */
function readErrorMessage(body: unknown): string | undefined {
  const message = (body as { message?: unknown } | null)?.message;
  if (Array.isArray(message)) return message.join('; ');
  return typeof message === 'string' ? message : undefined;
}

function jsonBody(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

export const health = {
  /** GET /healthz */
  get: (signal?: AbortSignal) => request<HealthResponse>('GET', '/healthz', { signal }),
};

/**
 * Session identity (Phase 9). A 401 here is meaningful — the caller decides
 * what to do (the layout shows the login state, it does not redirect).
 */
export const auth = {
  /** Identity probe. `{ authenticated: false }` is an answer, not an error. */
  me: (signal?: AbortSignal) => request<AuthMe>('GET', '/auth/me', { signal }),
  /**
   * Full-page navigation (never fetch()): the api destroys the session and then
   * 302s to `<web origin>/logged-out`, the interstitial that stands between the
   * dead session and the still-live Yandex one. A `fetch()` here would destroy
   * the session and then swallow the redirect chain in the background, leaving
   * the page — and the user — exactly where they were.
   */
  logout: () => {
    window.location.assign(`${API_BASE_URL}/auth/logout`);
  },
};

export const config = {
  /** GET /config/public */
  public: (signal?: AbortSignal) => request<PublicConfig>('GET', '/config/public', { signal }),
};

export interface SubscriptionFilters {
  status?: SubscriptionStatus;
  groupId?: string;
  q?: string;
  expiringWithinDays?: number;
  page?: number;
  limit?: number;
}

export interface SubscriptionInput {
  groupId?: string | null;
  name: string;
  url?: string | null;
  purpose?: string | null;
  initialCost: number;
  renewalCost: number;
  renewalPeriod?: RenewalPeriod;
  currency: string;
  expiresAt: string;
  expiresHighlightAfter?: string | null;
  status?: SubscriptionStatus;
  blockedReason?: string | null;
  note?: string | null;
  assignedUserEmails?: string[];
}

export const subscriptions = {
  list: (filters: SubscriptionFilters = {}, signal?: AbortSignal) =>
    request<Paginated<Subscription>>('GET', `/subscriptions${buildQuery(filters)}`, { signal }),
  get: (id: string, signal?: AbortSignal) =>
    request<Subscription>('GET', `/subscriptions/${id}`, { signal }),
  create: (input: SubscriptionInput) =>
    request<Subscription>('POST', '/subscriptions', jsonBody(input)),
  update: (id: string, input: Partial<SubscriptionInput>) =>
    request<Subscription>('PATCH', `/subscriptions/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  block: (id: string, reason: string) =>
    request<Subscription>('PATCH', `/subscriptions/${id}/block`, jsonBody({ reason })),
  unblock: (id: string) => request<Subscription>('PATCH', `/subscriptions/${id}/unblock`, { method: 'PATCH' }),
  remove: (id: string) => request<void>('DELETE', `/subscriptions/${id}`, { method: 'DELETE' }),
};

export const credentials = {
  set: (subscriptionId: string, input: { login: string; password: string; extra?: string | null }) =>
    request<{ subscriptionId: string }>(
      'PUT',
      `/subscriptions/${subscriptionId}/credentials`,
      jsonBody(input),
    ),
  /** Explicit, audit-logged decryption path. */
  view: (subscriptionId: string, signal?: AbortSignal) =>
    request<PlaintextCredentials>('GET', `/subscriptions/${subscriptionId}/credentials`, { signal }),
  remove: (subscriptionId: string) =>
    request<void>('DELETE', `/subscriptions/${subscriptionId}/credentials`, { method: 'DELETE' }),
};

export interface PaymentFilters {
  subscriptionId?: string;
  from?: string;
  to?: string;
  flow?: PaymentFlow;
  type?: TransactionType;
  page?: number;
  limit?: number;
}

export interface TransactionInput {
  type: TransactionType;
  occurredAt?: string;
  amount: number;
  currency: string;
  /**
   * Optional since the API's task #14: when it is omitted the leg is attributed
   * from its own receipt hints (card tail + currency), so a leg with a bill
   * books itself without the caller naming an account. No hints at all → the
   * API answers 400 and the caller has to name one.
   */
  accountId?: string | null;
  fxRate?: number;
  rateSource?: 'BILL' | 'SCREENSHOT' | 'MANUAL';
  rubAmount?: number;
  commission?: number;
  sourceTxnNo?: string;
  /** Store no receipt anchor for this leg — the receipt № is being reused. */
  allowDuplicate?: boolean;
}

export interface PaymentInput {
  subscriptionId: string;
  occurredAt: string;
  flow: PaymentFlow;
  paidAmount: number;
  paidCurrency: string;
  note?: string | null;
  transactions?: TransactionInput[];
  /** Skips the receipt dedup for every leg of the event (see TransactionInput). */
  allowDuplicate?: boolean;
}

/**
 * A partial transaction patch. Only the fields actually sent change; the rest
 * keep their stored value. Re-pointing a leg's account — or re-typing it, which
 * is how a misfiled card funding becomes a commission in the report (there is
 * no COMMISSION value: the split is derived from the legs) — never touches its
 * amount, date, rate or bills. The endpoint re-derives the parent event's
 * `rubTotal` in the same transaction either way.
 */
export interface TransactionPatch {
  accountId?: string;
  type?: TransactionType;
}

export const payments = {
  list: (filters: PaymentFilters = {}, signal?: AbortSignal) =>
    request<Paginated<PaymentEvent>>('GET', `/payments${buildQuery(filters)}`, { signal }),
  get: (id: string, signal?: AbortSignal) => request<PaymentEvent>('GET', `/payments/${id}`, { signal }),
  create: (input: PaymentInput) => request<PaymentEvent>('POST', '/payments', jsonBody(input)),
  update: (id: string, input: Partial<PaymentInput>) =>
    request<PaymentEvent>('PATCH', `/payments/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  updateTransaction: (id: string, patch: TransactionPatch) =>
    request<Transaction>('PATCH', `/transactions/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  /** POST /payments/:id/transactions — appends one leg, re-derives `rubTotal`. */
  addTransaction: (eventId: string, input: TransactionInput) =>
    request<PaymentEvent>('POST', `/payments/${eventId}/transactions`, jsonBody(input)),
  /** DELETE /transactions/:id — the parent event's `rubTotal` is re-derived. */
  removeTransaction: (id: string) =>
    request<void>('DELETE', `/transactions/${id}`, { method: 'DELETE' }),
  remove: (id: string) => request<void>('DELETE', `/payments/${id}`, { method: 'DELETE' }),
};

export interface MoneyAccountInput {
  kind: AccountKind;
  name: string;
  currency: string;
  meta?: Record<string, unknown> | null;
}

/**
 * GET /forecast — the renewal-derived months still ahead of the window's
 * actual payments. Read-only, so it never needs more than a VIEW role.
 */
export const forecast = {
  list: (params: { from: string; to?: string }, signal?: AbortSignal) =>
    request<ForecastResponse>('GET', `/forecast${buildQuery(params)}`, { signal }),
};

export const moneyAccounts = {
  list: (params: { page?: number; limit?: number; q?: string } = {}, signal?: AbortSignal) =>
    request<Paginated<MoneyAccount>>('GET', `/money-accounts${buildQuery(params)}`, { signal }),
  create: (input: MoneyAccountInput) => request<MoneyAccount>('POST', '/money-accounts', jsonBody(input)),
  update: (id: string, input: Partial<MoneyAccountInput>) =>
    request<MoneyAccount>('PATCH', `/money-accounts/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  remove: (id: string) => request<void>('DELETE', `/money-accounts/${id}`, { method: 'DELETE' }),
};

export interface VendorGroupInput {
  name: string;
  note?: string | null;
}

export const vendorGroups = {
  list: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
    request<Paginated<VendorGroup>>('GET', `/vendor-groups${buildQuery(params)}`, { signal }),
  create: (input: VendorGroupInput) => request<VendorGroup>('POST', '/vendor-groups', jsonBody(input)),
  update: (id: string, input: Partial<VendorGroupInput>) =>
    request<VendorGroup>('PATCH', `/vendor-groups/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  remove: (id: string) => request<void>('DELETE', `/vendor-groups/${id}`, { method: 'DELETE' }),
};

export const bills = {
  /**
   * POST /bills — multipart upload, OCR starts right away. `transactionId`
   * attaches the bill to an existing transaction leg.
   */
  upload: async (file: File, transactionId?: string): Promise<BillUploadResult> => {
    const form = new FormData();
    form.append('file', file, file.name);
    const response = await fetch(`${API_BASE_URL}/bills${buildQuery({ transactionId })}`, {
      method: 'POST',
      body: form,
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'POST', '/bills', await parseError(response));
    }
    return (await response.json()) as BillUploadResult;
  },
  get: (id: string, signal?: AbortSignal) => request<BillFile>('GET', `/bills/${id}`, { signal }),
  /** 302 -> presigned GET URL of the private object. */
  contentUrl: (id: string): string => `${API_BASE_URL}/bills/${id}/content`,
  confirm: (
    id: string,
    overrides: {
      amount?: number;
      currency?: string;
      occurredAt?: string;
      fxRate?: number | null;
      rateSource?: 'BILL' | 'SCREENSHOT' | 'MANUAL';
      commission?: number | null;
      sourceTxnNo?: string | null;
      subscriptionId?: string | null;
    } = {},
  ) => request<BillFile>('PATCH', `/bills/${id}/confirm`, { ...jsonBody(overrides), method: 'PATCH' }),
  reprocess: (id: string) => request<BillFile>('POST', `/bills/${id}/reprocess`, { method: 'POST' }),
  remove: (id: string) => request<void>('DELETE', `/bills/${id}`, { method: 'DELETE' }),
};

export const reports = {
  reimbursement: (from: string, to?: string, signal?: AbortSignal) =>
    request<ReimbursementReport>('GET', `/reports/reimbursement${buildQuery({ from, to })}`, { signal }),
};

/**
 * The balance read is windowed: `from` / `to` filter **both** sides of
 * `funded − spent` on `occurredAt`, while `balance` itself always covers the
 * whole window (paging never changes it). The web only ever reads the report
 * period — there is no all-time toggle.
 */
export interface BalanceFilters {
  from?: string;
  to?: string;
  perMonth?: boolean;
  page?: number;
  limit?: number;
}

/**
 * What the ledger accepts from the browser. `rubAmount` is deliberately absent
 * — the API derives it (`amount` for RUB, `amount × fxRate` otherwise) and
 * refuses to read it from a body.
 */
export interface BalanceEntryInput {
  kind: BalanceEntryKind;
  amount: number;
  currency?: string;
  occurredAt?: string;
  /** Required whenever `currency` is not RUB. */
  fxRate?: number;
  rateSource?: RateSource;
  note?: string | null;
}

export const balance = {
  get: (filters: BalanceFilters = {}, signal?: AbortSignal) =>
    request<BalanceReport>('GET', `/balance${buildQuery(filters)}`, { signal }),
  createEntry: (input: BalanceEntryInput) =>
    request<BalanceEntry>('POST', '/balance/entries', jsonBody(input)),
  updateEntry: (id: string, input: Partial<BalanceEntryInput>) =>
    request<BalanceEntry>('PATCH', `/balance/entries/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  removeEntry: (id: string) => request<void>('DELETE', `/balance/entries/${id}`, { method: 'DELETE' }),
};

export interface NotificationFilters {
  unread?: boolean;
  kind?: NotificationKind;
  subscriptionId?: string;
  page?: number;
  limit?: number;
}

export const notifications = {
  list: (filters: NotificationFilters = {}, signal?: AbortSignal) =>
    request<Paginated<Notification>>('GET', `/notifications${buildQuery(filters)}`, { signal }),
  unreadCount: (signal?: AbortSignal) =>
    request<{ count: number }>('GET', '/notifications/unread-count', { signal }),
  markRead: (id: string) => request<Notification>('PATCH', `/notifications/${id}/read`, { method: 'PATCH' }),
  markAllRead: () => request<{ updated: number }>('POST', '/notifications/read-all', jsonBody({})),
  /** Same pass the daily cron runs — handy for "refresh now" in dev. */
  scan: () => request<{ created: number; refreshed: number; resolved: number }>(
    'POST',
    '/notifications/scan',
    jsonBody({}),
  ),
};

/**
 * Account management (TASKS.md B4) — the api refuses everything here unless the
 * session carries ADMIN, so a stale UI only ever sees a 403, never a surprise.
 */
export interface UserInput {
  email: string;
  role?: AppRole;
}

export interface UserPatch {
  role?: AppRole;
  active?: boolean;
}

export const users = {
  list: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
    request<Paginated<AppUser>>('GET', `/users${buildQuery(params)}`, { signal }),
  create: (input: UserInput) => request<AppUser>('POST', '/users', jsonBody(input)),
  update: (id: string, input: UserPatch) =>
    request<AppUser>('PATCH', `/users/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  remove: (id: string) => request<void>('DELETE', `/users/${id}`, { method: 'DELETE' }),
};

/** `GET /audit` filters — every one optional, combinable, and ADMIN-gated. */
export interface AuditFilters {
  user?: string;
  action?: string;
  entityType?: string;
  from?: string;
  to?: string;
  subscriptionId?: string;
  page?: number;
  limit?: number;
}

export const audit = {
  list: (filters: AuditFilters = {}, signal?: AbortSignal) =>
    request<Paginated<AuditEntry>>('GET', `/audit${buildQuery(filters)}`, { signal }),
  /** Distinct actions, for the filter dropdown. */
  actions: (signal?: AbortSignal) => request<string[]>('GET', '/audit/actions', { signal }),
};

/** `attachment; filename="subs-mgmt-backup-….json"` → the filename alone. */
function filenameFromDisposition(disposition: string | null): string | null {
  const match = /filename="?([^";]+)"?/.exec(disposition ?? '');
  return match?.[1] ?? null;
}

/**
 * Settings → Backup (ADMIN): `request()` parses JSON bodies, but the export is
 * a DOWNLOAD — the blob is handed to the caller (`downloadBlob` in lib/csv.ts)
 * together with the filename the api picked. A 401 fires the same
 * session-expired event as every other endpoint.
 */
export const backup = {
  download: async (): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(`${API_BASE_URL}/backup`);
    if (response.status === 401) handleUnauthorized('/backup');
    if (!response.ok) {
      throw new ApiError(response.status, 'GET', '/backup', await parseError(response));
    }
    const blob = await response.blob();
    const fallback = `subs-mgmt-backup-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    return {
      blob,
      filename: filenameFromDisposition(response.headers.get('Content-Disposition')) ?? fallback,
    };
  },
};
